import { Button } from "@/components/ui/button";
import {
  MISSION_COPPER_INK,
  MISSION_HINT_INK,
  drivingBannerText,
  drivingStepLabel,
  requestCancelMissionDrive,
  invokeOfficialThinkStop,
  getMissionDrive,
  resumeMissionDrive,
  subscribeMissionDrive,
  type MissionDriveSnapshot,
} from "@/lib/mission-drive";
import { Loader2 } from "lucide-react";
import { useEffect, useState } from "react";

export function PageAgentBar({ onStop, onResume }: { onStop?: () => void; onResume?: () => void }) {
  const [drive, setDrive] = useState<MissionDriveSnapshot>(() => getMissionDrive());

  useEffect(() => subscribeMissionDrive(setDrive), []);

  if (!drive.driving && !drive.paused && !drive.cancelling) return null;

  const action = drive.action ?? "think";

  return (
    <div
      className="flex h-[72px] shrink-0 flex-col justify-center border-b border-border bg-card px-3"
      data-testid="page-agent-bar"
      data-mission-ignore=""
    >
      <div className="flex min-w-0 items-center gap-2">
        {drive.paused ? null : (
          <Loader2 className="size-3.5 shrink-0 animate-spin" style={{ color: MISSION_COPPER_INK }} aria-hidden />
        )}
        <span
          className="max-w-[40%] shrink-0 truncate text-[10px] font-semibold tracking-[0.08em] uppercase"
          style={{ color: MISSION_COPPER_INK }}
        >
          {drive.paused ? "Paused" : action}
        </span>
        {drivingStepLabel(drive) ? (
          <span className="shrink-0 font-mono text-[11px] text-muted-foreground" data-testid="page-agent-step">
            {drivingStepLabel(drive)}
          </span>
        ) : null}
        <div className="min-w-0 flex-1" aria-hidden data-testid="page-agent-segments">
          <span className="block h-1 w-full rounded-[1px] bg-[var(--copper)]" />
        </div>
        {drive.paused ? (
          <Button
            type="button"
            size="xs"
            variant="outline"
            className="h-6 px-2 text-[10px] tracking-[0.06em] uppercase"
            data-testid="page-agent-resume"
            onClick={() => {
              resumeMissionDrive();
              onResume?.();
            }}
          >
            Resume
          </Button>
        ) : (
          <Button
            type="button"
            size="xs"
            variant="outline"
            className="h-6 px-2 text-[10px] tracking-[0.06em] uppercase"
            data-testid="page-agent-stop"
            onClick={() => {
              requestCancelMissionDrive("stop");
              invokeOfficialThinkStop();
              onStop?.();
            }}
          >
            Stop
          </Button>
        )}
      </div>
      {drive.paused ? null : (
        <div className="mt-1 flex min-w-0 items-center gap-2">
          <p
            className="min-w-0 flex-1 truncate font-mono text-[11px]"
            style={{ color: MISSION_COPPER_INK }}
            data-testid="think-driving-banner"
          >
            {drivingBannerText(drive)}
          </p>
          <span
            className="shrink-0 font-mono text-[10px]"
            style={{ color: MISSION_HINT_INK }}
            data-testid="page-agent-esc"
          >
            Esc take over
          </span>
        </div>
      )}
    </div>
  );
}
